export const VIDEO_EXPORT_PROFILES = {
  "720p": {
    id: "720p",
    label: "720p (HD)",
    width: 1280,
    height: 720,
    frameRate: 30,
  },
  "1080p": {
    id: "1080p",
    label: "1080p (Full HD)",
    width: 1920,
    height: 1080,
    frameRate: 30,
  },
  "2160p": {
    id: "2160p",
    label: "2160p (4K UHD)",
    width: 3840,
    height: 2160,
    frameRate: 30,
  },
} as const;

export type VideoExportProfileId = keyof typeof VIDEO_EXPORT_PROFILES;
export type VideoExportProfile = (typeof VIDEO_EXPORT_PROFILES)[VideoExportProfileId];

export const VIDEO_EXPORT_PROFILE_LIST = Object.values(VIDEO_EXPORT_PROFILES);

type Size = {width: number; height: number};

export type FittedVideoExportProfile = Size & {scale: number};

function roundToEvenWithin(value: number, maximum: number): number {
  const rounded = Math.round(value / 2) * 2;
  const evenMaximum = Math.floor(maximum / 2) * 2;
  return Math.max(2, Math.min(rounded, evenMaximum));
}

/** Fit a template inside the selected landscape or portrait video envelope. */
export function fitVideoExportProfile(
  source: Size,
  profile: VideoExportProfile
): FittedVideoExportProfile {
  if (
    !Number.isFinite(source.width) ||
    !Number.isFinite(source.height) ||
    source.width <= 0 ||
    source.height <= 0
  ) {
    throw new Error(`Invalid video source dimensions ${source.width}x${source.height}`);
  }

  const portrait = source.height > source.width;
  const maximumWidth = portrait ? profile.height : profile.width;
  const maximumHeight = portrait ? profile.width : profile.height;
  const scale = Math.min(maximumWidth / source.width, maximumHeight / source.height);

  return {
    width: roundToEvenWithin(source.width * scale, maximumWidth),
    height: roundToEvenWithin(source.height * scale, maximumHeight),
    scale,
  };
}

export function getVideoExportProfile(value: unknown): VideoExportProfile {
  if (typeof value !== "string") {
    throw new Error("Video export profile must be a string");
  }
  if (!Object.prototype.hasOwnProperty.call(VIDEO_EXPORT_PROFILES, value)) {
    throw new Error(`Unsupported video export profile ${JSON.stringify(value)}`);
  }
  return VIDEO_EXPORT_PROFILES[value as VideoExportProfileId];
}
